import { AlertCircleIcon, Loader2Icon, PhoneCallIcon, ShieldCheckIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

export interface VerificationStepCardProps {
	verificationRequestFailed: boolean
	resendCooldownRemaining: number
	verificationAutoAttempts: number
	verificationOnHold: boolean
}

export function VerificationStepCard({
	verificationRequestFailed,
	resendCooldownRemaining,
	verificationAutoAttempts,
	verificationOnHold,
}: VerificationStepCardProps) {
	const { t } = useTranslation('common')
	const [cooldown, setCooldown] = useState(resendCooldownRemaining)

	useEffect(() => {
		setCooldown(resendCooldownRemaining)
	}, [resendCooldownRemaining])

	useEffect(() => {
		if (cooldown <= 0) {
			return
		}
		const timer = setInterval(() => {
			setCooldown((prev) => Math.max(0, prev - 1))
		}, 1000)
		return () => clearInterval(timer)
	}, [cooldown])

	if (verificationRequestFailed) {
		return (
			<div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
				<div className="flex items-start gap-4">
					<div className="size-12 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
						<AlertCircleIcon className="size-6 text-amber-600" />
					</div>
					<div className="flex-1 space-y-2">
						<h3 className="font-medium text-gray-900">
							{t('whatsappSettings.verification.stalledTitle')}
						</h3>
						<p className="text-sm text-amber-800">
							{t('whatsappSettings.verification.stalledDescription')}
						</p>
					</div>
				</div>
			</div>
		)
	}

	const attemptNumber = verificationAutoAttempts + 1

	const waitingLine = verificationOnHold
		? t('whatsappSettings.verification.onHold', {
			minutes: Math.max(1, Math.ceil(cooldown / 60)),
		})
		: cooldown <= 0
		? t('whatsappSettings.verification.requestingShortly')
		: cooldown > 120
		? t('whatsappSettings.verification.waitingForCallLong', {
			attempt: attemptNumber,
			minutes: Math.ceil(cooldown / 60),
		})
		: t('whatsappSettings.verification.waitingForCall', {
			count: cooldown,
			attempt: attemptNumber,
		})

	return (
		<div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
			<div className="flex items-start gap-4">
				<div className="size-12 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
					<PhoneCallIcon className="size-6 text-blue-600 animate-pulse" />
				</div>
				<div className="flex-1 space-y-3">
					<div>
						<h3 className="font-medium text-gray-900 mb-1">
							{t('whatsappSettings.verification.title')}
						</h3>
						<p className="text-sm text-gray-700">
							{t('whatsappSettings.verification.callingNumber')}
						</p>
					</div>

					<div
						className={`flex items-center gap-2 text-xs ${
							verificationOnHold ? 'text-amber-700' : 'text-blue-700'
						}`}
					>
						<Loader2Icon className="size-3 animate-spin" />
						<span>{waitingLine}</span>
					</div>

					{verificationAutoAttempts > 0 && (
						<div className="flex items-center gap-2 text-xs text-amber-700">
							<ShieldCheckIcon className="size-3" />
							<span>
								{t('whatsappSettings.verification.previousAttemptsHint', {
									count: verificationAutoAttempts,
								})}
							</span>
						</div>
					)}
				</div>
			</div>
		</div>
	)
}
